import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getProjectEntitlements, getProjectModulesRegistry } from '../../api/entitlements';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import { useAdminPanelTheme } from '../../project-modules/store/AdminPanelThemeContext';
import { buildStoreCatalogFromRegistry } from '../../project-modules/store/buildStoreCatalogFromRegistry';
import ProjectCostSummary from '../../project-modules/store/ProjectCostSummary';
import ProjectBillingPanel from './ProjectBillingPanel';
import { STORE_QUOTA_PACKAGES } from '../../project-modules/store/storeListings';
import { buildStoreUrl } from '../../project-modules/store/storeUrls';
import HelpHint from '../../components/HelpHint';

function formatQuotaValue(value, unlimitedLabel) {
  if (value === null || value === undefined) return unlimitedLabel;
  return String(value);
}

function quotaLabel(code) {
  return STORE_QUOTA_PACKAGES.find((p) => p.code === code)?.label || code;
}

export default function CabinetProjectInfoDialog({
  open,
  project,
  onClose,
  billingSummary,
}) {
  const navigate = useNavigate();
  const { sa, moduleStore: ms } = usePublicUiLocale();
  const { mode: themeMode } = useAdminPanelTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entitlements, setEntitlements] = useState(null);
  const [registry, setRegistry] = useState(null);

  const slug = project?.slug || '';

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const [ent, reg] = await Promise.all([
        getProjectEntitlements(slug),
        getProjectModulesRegistry(),
      ]);
      setEntitlements(ent);
      setRegistry(reg);
    } catch (e) {
      setEntitlements(null);
      setRegistry(null);
      setError(e?.response?.data?.message || e?.message || sa.cabinetProjectInfoLoadFailed);
    } finally {
      setLoading(false);
    }
  }, [slug, sa.cabinetProjectInfoLoadFailed]);

  useEffect(() => {
    if (!open || !slug) return;
    load();
  }, [open, slug, load]);

  const catalog = useMemo(
    () => buildStoreCatalogFromRegistry(registry, entitlements),
    [registry, entitlements],
  );

  const plan = useMemo(
    () => (registry?.plans || []).find((p) => p.code === entitlements?.plan),
    [registry, entitlements?.plan],
  );

  const addonModules = useMemo(
    () => (registry?.modules || []).filter((m) => m.tier === 'addon'),
    [registry],
  );

  const projectBilling = useMemo(
    () => billingSummary?.projects?.find((p) => p.slug === slug) || null,
    [billingSummary, slug],
  );

  const isSuspended = Boolean(projectBilling?.billing_suspended);

  const handleOpenProject = () => {
    if (!slug || isSuspended) return;
    onClose();
    navigate(`/v2/superadmin/project/${encodeURIComponent(slug)}`);
  };

  const handleOpenStore = () => {
    if (!slug) return;
    onClose();
    navigate(buildStoreUrl(slug));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {sa.projectInfoTitle}
        {project ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {project.name}
            {' · '}
            {project.slug}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {loading && !entitlements ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {billingSummary ? (
          <ProjectBillingPanel
            billing={projectBilling}
            accountSummary={billingSummary}
            projectSlug={slug}
          />
        ) : null}

        {entitlements && registry ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {sa.planLabel}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Chip size="small" label={plan?.name || entitlements.plan || '—'} color="primary" variant="outlined" />
                {plan?.description ? (
                  <Typography variant="body2" color="text.secondary">
                    {plan.description}
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                {sa.cabinetProjectInfoUids} ({(project?.uids || []).length})
              </Typography>
              {(project?.uids || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  —
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {(project?.uids || []).map((u) => (
                    <Chip
                      key={u.id || u.code}
                      size="small"
                      variant="outlined"
                      label={u.name || u.code}
                      title={u.code}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                {sa.cabinetProjectInfoModules}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                {addonModules.map((mod) => {
                  const enabled = Boolean(entitlements.modules?.[mod.code]);
                  return (
                    <Chip
                      key={mod.code}
                      size="small"
                      label={mod.name}
                      color={enabled ? 'success' : 'default'}
                      variant={enabled ? 'filled' : 'outlined'}
                      title={enabled ? sa.cabinetProjectInfoModuleOn : sa.cabinetProjectInfoModuleOff}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">
                  {sa.cabinetProjectInfoQuotas}
                </Typography>
                <HelpHint title={sa.helpCabinetQuotasTitle} text={sa.helpCabinetQuotas} />
              </Box>
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                {(registry.quotas || []).map((q) => {
                  const current = entitlements.quotas?.[q.code];
                  const planDefault = plan?.quotas?.[q.code];
                  const upgraded =
                    current !== planDefault &&
                    (current === null || planDefault === null || Number(current) > Number(planDefault));
                  return (
                    <Stack
                      key={q.code}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">
                          {quotaLabel(q.code)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sa.cabinetProjectInfoQuotaPlan}: {formatQuotaValue(planDefault, sa.cabinetProjectInfoUnlimited)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={formatQuotaValue(current, sa.cabinetProjectInfoUnlimited)}
                        color={upgraded ? 'warning' : 'default'}
                        variant={upgraded ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>

            {catalog ? (
              <>
                <Divider />
                <ProjectCostSummary
                  catalog={catalog}
                  entitlements={catalog.entitlements}
                  cart={null}
                  locale={ms}
                  context="admin"
                  variant="compact"
                  themeMode={themeMode}
                  onOpenStore={handleOpenStore}
                />
              </>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{sa.cancel}</Button>
        <Button
          variant="outlined"
          startIcon={<StorefrontIcon />}
          onClick={handleOpenStore}
          disabled={!slug}
        >
          {sa.projectStore}
        </Button>
        <Button
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenProject}
          disabled={!slug || isSuspended}
          title={isSuspended ? sa.billingProjectSuspended : undefined}
        >
          {sa.openProject}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CabinetProjectInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  project: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  billingSummary: PropTypes.object,
};

CabinetProjectInfoDialog.defaultProps = {
  project: null,
  billingSummary: null,
};
